import React, { useEffect, useState, useRef, Fragment, lazy, createElement } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, Info } from 'lucide-react';
import { PricingModal } from './PricingModal';
const barbers = [{
  id: 2,
  name: 'Scott Suarez',
  specialty: 'Barba & Diseños Urbanos',
  image: 'https://cdn.magicpatterns.com/uploads/8cmkR2MXJzTgoeF32RhU1M/heylloyd-image-1782484405895.webp',
  calendlyUrl: 'https://calendly.com/scottsuarez31/reserva?hide_event_type_details=1&hide_gdpr_banner=1&background_color=ffffff&text_color=000000&primary_color=006bff'
}, {
  id: 3,
  name: 'Mateo Silva',
  specialty: 'Barba & Estilo Clásico',
  image: 'https://cdn.magicpatterns.com/uploads/qZkAqHc58V1bvVq8k3f4WA/heylloyd-image-1782484509511.webp',
  calendlyUrl: 'https://calendly.com/mateosilva2301/reserva?hide_event_type_details=1&hide_gdpr_banner=1&background_color=ffffff&text_color=000000&primary_color=c5a880'
}, {
  id: 1,
  name: 'Nicolas Marshall',
  specialty: 'Colorimetría & Cortes de Autor',
  image: 'https://cdn.magicpatterns.com/uploads/3Jhm5cFB1zhvWv2dVwZjUb/heylloyd-image-1782484282301.webp',
  calendlyUrl: 'https://calendly.com/nicolasmarshall7/reserva?hide_event_type_details=1&hide_gdpr_banner=1&background_color=ffffff&text_color=000000&primary_color=eab308'
}];
export function BookingSystem() {
  const [step, setStep] = useState(1);
  const [selectedBarber, setSelectedBarber] = useState<number | null>(null);
  const [isPricingModalOpen, setIsPricingModalOpen] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const scrollToSection = () => {
    if (sectionRef.current) {
      const yOffset = -80; // Adjust for navbar height
      const y = sectionRef.current.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({
        top: y,
        behavior: 'smooth'
      });
    }
  };
  const handleBarberSelect = (id: number) => {
    setSelectedBarber(id);
    setStep(2);
    scrollToSection();
  };
  // Load Calendly script when step 2 is active
  useEffect(() => {
    if (step === 2) {
      const script = document.createElement('script');
      script.src = 'https://assets.calendly.com/assets/external/widget.js';
      script.async = true;
      script.defer = true;
      document.body.appendChild(script);
      return () => {
        document.body.removeChild(script);
      };
    }
  }, [step]);
  return <section id="reservar" ref={sectionRef} className="py-32 bg-[#0a0a0a] border-t border-white/5 relative overflow-hidden" data-id="element-71" data-mp="1ar20ss812o7r">
      {/* Subtle gold glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-3xl h-64 bg-[#c9a84c]/5 rounded-full blur-[120px] pointer-events-none transform-gpu will-change-transform" data-id="element-72" data-mp="1khej0dsde5aq" />

      <div className="max-w-4xl mx-auto px-6 md:px-12 relative z-10" data-id="element-73" data-mp="1tiur2evppvbs">
        <div className="text-center mb-12" data-id="element-74" data-mp="1tgi2hivg8mpr">
          <h2 className="font-display text-5xl md:text-6xl mb-4 text-white" data-id="element-75" data-mp="2d44qsg57us6m">
            Reserva tu <span className="text-[#c9a84c]" data-id="element-76" data-mp="2i40vkhlqmnn8">Turno</span>
          </h2>
          <p className="text-gray-400 text-lg mb-6" data-id="element-77" data-mp="2i14yswlmm50d">
            Selecciona tu barbero, elige el horario y asegura tu lugar.
          </p>
          <button onClick={() => setIsPricingModalOpen(true)} className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-white/5 border border-white/10 hover:bg-white/10 hover:border-[#c9a84c]/50 text-gray-300 hover:text-white transition-all text-sm font-medium backdrop-blur-sm" data-id="element-78" data-mp="284if4b51djzx">
            <Info size={16} className="text-[#c9a84c]" data-id="element-79" data-mp="1kha91qzsa6wu" />
            Ver lista de precios
          </button>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center mb-12 max-w-[200px] mx-auto" data-id="element-80" data-mp="1bfasezsol4uf">
          {[1, 2].map(i => <Fragment key={i} data-id="element-81" data-mp="1fgvzu5mg4qx8">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm transition-colors duration-300 ${step >= i ? 'bg-[#c9a84c] text-black' : 'bg-white/5 text-gray-500 border border-white/10'}`} data-id="element-82" data-mp="1fg4b9er9ntgg">
                {i}
              </div>
              {i < 2 && <div className={`flex-1 h-[2px] transition-colors duration-300 ${step > i ? 'bg-[#c9a84c]' : 'bg-white/10'}`} data-id="element-83" data-mp="tojjrxc3ksj3" />}
            </Fragment>)}
        </div>

        <div className="bg-white/[0.02] border border-white/10 rounded-3xl p-6 md:p-10 min-h-[500px] relative" data-id="element-84" data-mp="knbxkdmuvi5d">
          <AnimatePresence mode="wait" data-id="element-85" data-mp="fptlwfhgeoto">
            {/* STEP 1: Select Barber */}
            {step === 1 && <motion.div key="step1" initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: 20
          }} className="space-y-6" data-id="element-86" data-mp="aqz3henef2a3">
                <h3 className="text-2xl font-semibold text-white mb-8 text-center" data-id="element-87" data-mp="2hkrfapb1tg9z">
                  Selecciona un Profesional
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-88" data-mp="2rbueflkf3l6m">
                  {barbers.map(barber => <div key={barber.id} className="bg-black/40 border border-white/10 rounded-2xl overflow-hidden hover:border-[#c9a84c]/50 transition-all duration-300 group" data-id="element-89" data-mp="1ummu2gvjbi4a">
                      <div className="aspect-[3/4] overflow-hidden bg-black" data-id="element-90" data-mp="23omap82n0k6p">
                        <img src={barber.image} alt={barber.name} className="w-full h-full object-contain grayscale group-hover:grayscale-0 transition-all duration-500" loading="lazy" decoding="async" width="406" height="728" data-id="element-91" data-mp="28n7bb8lsx5vo" />
                      </div>
                      <div className="p-6 text-center" data-id="element-92" data-mp="34bd3lr98tfkq">
                        <h4 className="text-xl font-bold text-white mb-1" data-id="element-93" data-mp="348qw1rmcr02i">
                          {barber.name}
                        </h4>
                        <p className="text-sm text-[#c9a84c] mb-6 h-10" data-id="element-94" data-mp="2blmnjco1w3lz">
                          {barber.specialty}
                        </p>
                        <button onClick={() => handleBarberSelect(barber.id)} className="w-full py-3 rounded-xl bg-white/5 hover:bg-[#c9a84c] hover:text-black text-white font-semibold transition-colors duration-300 border border-white/10 hover:border-[#c9a84c]" data-id="element-95" data-mp="26ow88rk2lc1m">
                          Seleccionar
                        </button>
                      </div>
                    </div>)}
                </div>
              </motion.div>}

            {/* STEP 2: Calendly Embed */}
            {step === 2 && <motion.div key="step2" initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: 20
          }} className="flex flex-col h-full" data-id="element-96" data-mp="872iialewipq">
                <div className="flex items-center gap-3 mb-4" data-id="element-97" data-mp="d4m6414027uc">
                  <button onClick={() => setStep(1)} aria-label="Volver a elegir profesional" className="flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-white/10 border border-white/20 text-white text-sm font-semibold hover:bg-[#c9a84c] hover:text-black hover:border-[#c9a84c] active:scale-95 transition-all shrink-0" data-id="element-98" data-mp="d42l2bj1z3c3">
                    <ChevronLeft size={18} data-id="element-99" data-mp="3thn4hiuf4mdx" />
                    Volver
                  </button>
                  <h3 className="text-lg md:text-2xl font-semibold text-white flex-1 text-center" data-id="element-100" data-mp="3omra4c1plmxn">
                    Agenda tu Cita
                  </h3>
                  <span className="hidden sm:block w-[92px] shrink-0" aria-hidden="true" data-id="element-101" data-mp="3fl06dcqqbq6f" />
                </div>

                <div className="w-full bg-white rounded-2xl overflow-hidden" data-id="element-102" data-mp="c3hzb7is6n72">
                  <iframe src={barbers.find(b => b.id === selectedBarber)?.calendlyUrl} width="100%" height="700px" frameBorder="0" title="Calendly Scheduling Page" className="block" loading="lazy" data-id="element-103" data-mp="c5m0vit7ja46"></iframe>
                </div>
              </motion.div>}
          </AnimatePresence>
        </div>
      </div>

      <PricingModal isOpen={isPricingModalOpen} onClose={() => setIsPricingModalOpen(false)} data-id="element-104" data-mp="17c3ia1qj0vyy" />
    </section>;
}